import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  CreateManualReceiptRequest,
  Currency,
  Participant,
  Trip,
} from "@/api/types";
import { TextInput } from "./TextInput";
import { CurrencyPicker } from "./CurrencyPicker";
import { PaidBySlot } from "./PaidBySlot";
import { ManualLineDraftRow } from "./ManualLineDraftRow";
import { MoneyAmount } from "./MoneyAmount";
import { Button } from "./Button";
import {
  canCreateManual,
  draftTotalCents,
  firstUnmetRequirement,
  isValidLine,
  parseManualAmount,
  type ManualLineDraft,
} from "@/utils/manualReceipt";
import "./manual-receipt-form.css";

export interface ManualReceiptFormProps {
  trip: Trip;
  participants: ReadonlyArray<Participant>;
  /** True while the create request is in flight (inputs go read-only). */
  submitting: boolean;
  /** Fires the manual-create call with the assembled inline-line request. */
  onCreate: (draft: CreateManualReceiptRequest) => void;
}

let rowSeq = 0;
function emptyRow(): ManualLineDraft {
  rowSeq += 1;
  return { id: `draft-${rowSeq}`, name: "", amount: "", qty: 1 };
}

/**
 * The manual create form (components.md §7.7, wireframe §B). Captures label +
 * currency + payer + ≥1 line, then hands an inline-line CreateManualReceipt
 * request to `onCreate`. No consumer tagging here — splitting happens on the
 * receipt-detail screen after creation (wireframe §Decisions D2), hence the
 * «Кого поділити — вкажеш на наступному екрані» hint.
 */
export function ManualReceiptForm({
  trip,
  participants,
  submitting,
  onCreate,
}: ManualReceiptFormProps) {
  const [vendorName, setVendorName] = useState("");
  const [ccy, setCcy] = useState<Currency>(trip.settlement_ccy);
  const [payerId, setPayerId] = useState<string | null>(null);
  const [lines, setLines] = useState<ManualLineDraft[]>(() => [emptyRow()]);
  const [attempted, setAttempted] = useState(false);
  const captionId = useRef(`manual-gate-${Math.random().toString(36).slice(2)}`).current;

  const totalCents = useMemo(() => draftTotalCents(lines), [lines]);
  const gateInput = { vendorName, payerId, lines };
  const canSubmit = canCreateManual(gateInput);
  const unmet = firstUnmetRequirement(gateInput);

  const payer = participants.find((p) => p.participant_id === payerId) ?? null;
  const noRoster = participants.length === 0;

  function patchLine(id: string, patch: Partial<ManualLineDraft>): void {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  function submit(): void {
    setAttempted(true);
    if (!canCreateManual(gateInput)) return;
    const inlineLines = lines.filter(isValidLine).map((l) => ({
      name: l.name.trim(),
      quantity: l.qty > 0 ? l.qty : 1,
      unit_price: parseManualAmount(l.amount)!,
    }));
    onCreate({
      vendor_name: vendorName.trim(),
      source_ccy: ccy,
      paid_by_participant_id: payerId!,
      lines: inlineLines,
    });
  }

  return (
    <form
      className="wp-mrf"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <header className="wp-mrf__head">
        <h1 className="wp-mrf__title">Витрата вручну</h1>
        <p className="wp-mrf__subtitle">
          Без фото. Просто запиши, хто за що заплатив.
        </p>
      </header>

      <TextInput
        label="Назва"
        placeholder="Напр., Вино, Таксі, Чайові"
        value={vendorName}
        error={attempted && vendorName.trim() === "" ? "Додай назву" : undefined}
        onChange={(e) => setVendorName(e.target.value)}
        disabled={submitting}
        autoFocus
        data-testid="manual-vendor-name"
      />

      <div className="wp-mrf__two-up">
        <CurrencyPicker
          label="Валюта"
          value={ccy}
          onChange={setCcy}
          disabled={submitting}
        />
        {noRoster ? (
          <div className="wp-mrf__no-roster" data-testid="manual-no-roster">
            <span className="wp-mrf__no-roster-label">Хто платив</span>
            <p className="wp-mrf__no-roster-note">Спочатку додай учасників.</p>
            <Link to={`/t/${trip.trip_id}/participants`} className="wp-mrf__no-roster-link">
              + Додати учасника
            </Link>
          </div>
        ) : (
          <PaidBySlot
            participant={payer}
            participants={participants}
            disabled={submitting}
            onChange={setPayerId}
          />
        )}
      </div>

      <section className="wp-mrf__lines" aria-label="Позиції">
        <h2 className="wp-mrf__overline">Позиції</h2>
        {lines.map((line, i) => (
          <ManualLineDraftRow
            key={line.id}
            index={i}
            name={line.name}
            amount={line.amount}
            qty={line.qty}
            ccy={ccy}
            removable={lines.length > 1}
            showErrors={attempted}
            onName={(v) => patchLine(line.id, { name: v })}
            onAmount={(v) => patchLine(line.id, { amount: v })}
            onQty={(v) => patchLine(line.id, { qty: v })}
            onRemove={() => setLines((prev) => prev.filter((l) => l.id !== line.id))}
          />
        ))}
        <button
          type="button"
          className="wp-mrf__add-line"
          onClick={() => setLines((prev) => [...prev, emptyRow()])}
          disabled={submitting}
          data-testid="manual-add-line"
        >
          + Додати позицію
        </button>
      </section>

      <div className="wp-mrf__totals">
        <span className="wp-mrf__total-label">Разом</span>
        <MoneyAmount value={totalCents / 100} ccy={ccy} size="h1" data-testid="manual-total" />
      </div>

      <p className="wp-mrf__hint">Кого поділити — вкажеш на наступному екрані.</p>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={submitting}
        disabled={!canSubmit}
        aria-describedby={!canSubmit && unmet ? captionId : undefined}
        data-testid="manual-create-submit"
      >
        Створити
      </Button>
      {!canSubmit && unmet ? (
        <p id={captionId} className="wp-mrf__gate-caption" data-testid="manual-gate-caption">
          {unmet}
        </p>
      ) : null}
    </form>
  );
}
